<template>
  <div class="content-toggle">
    <div class="toggle">
      <span
        v-if="active === 'filters' && activeAction === 'openFilters'"
        role="button"
        tabindex="0"
        class="toggle-tab active"
        aria-label="Filters"
        @click="openFilters"
        @keyup.enter="openFilters"
        @keyup.space="openFilters"
      >
        <i class="material-symbols-outlined fill" aria-hidden="true" data-icon="filter_alt"></i>
        <span class="toggle-text">Filters</span>
      </span>
      <router-link
        v-else
        :to="filterUrl"
        class="toggle-tab"
        :class="active === 'filters' ? 'active' : ''"
        @click="updateUserAndNav"
      >
        <i class="material-symbols-outlined fill" aria-hidden="true" data-icon="filter_alt"></i>
        <span class="toggle-text">Filters</span>
      </router-link>
      <span
        v-if="browseUrl && active === 'collections' && activeAction === 'openFilters'"
        role="button"
        tabindex="0"
        class="toggle-tab active"
        aria-label="Collections"
        @click="openFilters"
        @keyup.enter="openFilters"
        @keyup.space="openFilters"
      >
        <i class="material-symbols-outlined fill" aria-hidden="true" data-icon="dashboard"></i>
        <span class="toggle-text">Collections</span>
      </span>
      <router-link
        v-else-if="browseUrl"
        :to="browseUrl"
        class="toggle-tab"
        :class="active === 'collections' ? 'active' : ''"
        data-tour="filter-collections-toggle-cta"
        @click="updateUserAndNav"
      >
        <i class="material-symbols-outlined fill" aria-hidden="true" data-icon="dashboard"></i>
        <span class="toggle-text">Collections</span>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Simple switch button nav with custom active actions
 */
import { useUserAuthStore } from '@/stores/user-auth'
import { computed } from 'vue'
import { fbApi } from '@/utils/http-api'
import { useSearchStore } from '@/stores/search'

type ViewType = Record<'videos' | 'plans' | 'myPrograms', string>

/** PROPS **/
const props = withDefaults(
  defineProps<{
    active?: 'filters' | 'collections'
    activeAction?: 'openFilters' | 'link'
    filterUrl: string
    browseUrl?: string
  }>(),
  {
    active: 'filters',
    activeAction: 'openFilters',
    browseUrl: '',
  },
)

/** STATE **/
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()

/** COMPUTED **/
// value to pass back to updateUser API and store for user.showBrowseView
const userShowBrowseView = computed(() => {
  return props.active !== 'collections' ? 1 : 0
})

/** METHODS **/
/**
 * Update the links with javascript for current SPA,
 * will be correctly set on page reloads.
 *
 * a[data-browse-view]
 * - videos
 * - plans
 * - myPrograms
 */
function toggleBrowseLinks() {
  // hrefs
  const filterViews: ViewType = {
    videos: '/videos',
    plans: '/plans',
    myPrograms: '/my/programs',
  }

  const browseViews: ViewType = {
    videos: '/videos/browse',
    plans: '/plans/browse',
    myPrograms: '/my/programs/browse',
  }

  const views = userShowBrowseView.value === 1 ? browseViews : filterViews
  // end hrefs

  // toggle nav and footer links in real time
  const toggleLinks = document.querySelectorAll('a[data-browse-view]')

  toggleLinks.forEach((link) => {
    if (
      link instanceof HTMLAnchorElement &&
      link.dataset.browseView &&
      views[link.dataset.browseView as keyof ViewType]
    ) {
      link.setAttribute('href', views[link.dataset.browseView as keyof ViewType])
    }
  })
  // end toggle links
}

function updateUserShowBrowseView() {
  // instantly update user store
  userAuthStore.updateUser({
    showBrowseView: userShowBrowseView.value,
  })

  try {
    // update users profile
    fbApi.put('/my/account', {
      skipErrorModal: true,
      json: {
        show_browse_view: userShowBrowseView.value,
      },
    })
  } catch (e) {
    // noop, not rolling back the user setting, but it is not saved either
  }
}

/**
 * Silently fire fetch request to update user.showBrowseView to remember the
 * last view the user was on (filter vs browse).
 */
function updateUserAndNav() {
  // if not logged in, or fb admin, do nothing
  if (!userAuthStore.user.isLoggedIn || userAuthStore.isFbAdminUser) {
    return
  }

  // fire, but do not wait for success before updating nav
  updateUserShowBrowseView()
  toggleBrowseLinks()
}

function openFilters() {
  searchStore.setFiltersOpen(true)
}
</script>
